import { useMemo, useState, useRef, useEffect } from "react";
import { X, Check } from "lucide-react";
import { BusinessService } from "../../../../types/businessService";
import { FormServiceRef } from "../../../../types/forms";
import { Button } from "../../../../components/ui/Button";

interface ServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  services: BusinessService[];
  selected: FormServiceRef[];
  onApply: (selected: FormServiceRef[]) => void;
}

function toFormServiceRef(bs: BusinessService): FormServiceRef {
  return {
    id: bs.id,
    title: bs.name,
    name: bs.name,
    slug: bs.slug || bs.name.toLowerCase().replace(/\s+/g, "-"),
  };
}

function CategoryRow({
  categoryName,
  allSelected,
  someSelected,
  onToggleCategory,
  items,
  localSelected,
  toggleOne,
}: {
  categoryName: string;
  allSelected: boolean;
  someSelected: boolean;
  onToggleCategory: () => void;
  items: BusinessService[];
  localSelected: Set<string>;
  toggleOne: (id: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 cursor-pointer group">
        <input
          ref={ref}
          type="checkbox"
          checked={allSelected}
          onChange={onToggleCategory}
          className="rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
        />
        <span className="text-sm font-medium text-gray-800 group-hover:text-[#885ABB]">
          {categoryName}
        </span>
      </label>
      <ul className="pl-6 space-y-1">
        {items.map((s) => {
          const id = String(s.id);
          const isSelected = localSelected.has(id);
          return (
            <li key={s.id}>
              <label className="flex items-center gap-2 cursor-pointer py-1 rounded hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleOne(id)}
                  className="rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                />
                <span className="text-sm text-gray-700">{s.name}</span>
                {s.durationLabel && (
                  <span className="text-xs text-gray-400">{s.durationLabel}</span>
                )}
                {isSelected && (
                  <Check className="w-3.5 h-3.5 text-[#885ABB] ml-auto" />
                )}
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function ServicesModal({
  isOpen,
  onClose,
  services,
  selected,
  onApply,
}: ServicesModalProps) {
  const selectedIds = useMemo(
    () => new Set(selected.map((s) => String(s.id))),
    [selected]
  );

  const [localSelected, setLocalSelected] = useState<Set<string>>(selectedIds);
  useEffect(() => {
    if (isOpen) setLocalSelected(selectedIds);
  }, [isOpen, selectedIds]);

  const categories = useMemo(() => {
    const map = new Map<string, BusinessService[]>();
    for (const s of services) {
      const cat = s.categoryName?.trim() || "Uncategorized";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(s);
    }
    return Array.from(map.entries()).sort(([a], [b]) =>
      a === "Uncategorized" ? 1 : b === "Uncategorized" ? -1 : a.localeCompare(b)
    );
  }, [services]);

  const toggleOne = (id: string) => {
    setLocalSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (categoryName: string) => {
    const items = categories.find(([name]) => name === categoryName)?.[1] || [];
    const ids = items.map((s) => String(s.id));
    const allSelected = ids.every((id) => localSelected.has(id));
    setLocalSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectAll = () => {
    setLocalSelected(new Set(services.map((s) => String(s.id))));
  };

  const clearAll = () => {
    setLocalSelected(new Set());
  };

  const handleApply = () => {
    const list = services.filter((s) => localSelected.has(String(s.id))).map(toFormServiceRef);
    onApply(list);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Select Services</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X size={20} />
          </button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50/80">
          <Button
            type="button"
            variant="ghost"
            onClick={selectAll}
            className="text-xs h-8 text-[#885ABB] hover:bg-purple-50"
          >
            Select all
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={clearAll}
            className="text-xs h-8 text-gray-600 hover:bg-gray-100"
          >
            Clear all
          </Button>
          <span className="text-xs text-gray-500 ml-auto">
            {localSelected.size} selected
          </span>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {categories.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No services available.</p>
          ) : (
            categories.map(([categoryName, items]) => {
              const ids = items.map((s) => String(s.id));
              const allSelected = ids.every((id) => localSelected.has(id));
              const someSelected = ids.some((id) => localSelected.has(id));
              return (
                <CategoryRow
                  key={categoryName}
                  categoryName={categoryName}
                  allSelected={allSelected}
                  someSelected={someSelected}
                  onToggleCategory={() => toggleCategory(categoryName)}
                  items={items}
                  localSelected={localSelected}
                  toggleOne={toggleOne}
                />
              );
            })
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-sm text-gray-600"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            className="text-sm bg-[#885ABB] hover:bg-[#7748A4] text-white px-4"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
